import React, {useState} from 'react'
import {Button, ParagraphText} from '@thryvlabs/maverick'

const fieldWrap = {
  display: 'flex',
  flexDirection: 'column',
  gap: 6,
  marginBottom: 12,
}
const fieldLabel = {
  fontSize: 12,
  fontWeight: 500,
  color: '#6B7280',
  marginBottom: 2,
}
const errorText = {fontSize: 11, color: '#DC2626', marginTop: 2}
const footerRow = {
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  marginTop: '1.25rem',
}

const tableStyle = {
  width: '100%',
  borderCollapse: 'collapse',
  marginTop: 10,
  border: '1px solid #E5E7EB',
  borderRadius: 8,
  overflow: 'hidden',
}
const tdCode = {
  padding: '8px 12px',
  fontFamily: 'monospace',
  fontSize: 12,
  color: '#374151',
}
const tdAction = {padding: '8px 12px', textAlign: 'right'}

export function AddGroupModal({onSave, onClose, existingGroups}) {
  const [name, setName] = useState('')
  const [code, setCode] = useState('')
  const [codes, setCodes] = useState([])

  const addCode = () => {
    const t = code.trim()
    if (t && !codes.includes(t)) {
      setCodes(c => [...c, t])
      setCode('')
    }
  }

  const isDupe = existingGroups.includes(name.trim())
  const canSave = name.trim() && !isDupe && codes.length > 0
  const noCodesYet = codes.length === 0

  return (
    <>
      <div style={fieldWrap}>
        <span style={fieldLabel}>Product group name</span>
        <input
          value={name}
          onChange={e => setName(e.target.value)}
          placeholder="e.g. NewProduct"
          style={{
            width: '100%',
            padding: '7px 10px',
            fontSize: 13,
            border: '1px solid #D1D5DB',
            borderRadius: 6,
            outline: 'none',
            fontFamily: 'inherit',
            background: '#fff',
            color: '#111',
            boxSizing: 'border-box',
          }}
        />
        {isDupe && (
          <span style={errorText}>A group with this name already exists.</span>
        )}
      </div>

      <div style={fieldWrap}>
        <span style={fieldLabel}>
          Plan codes <span style={{color: '#DC2626'}}>*</span>
        </span>
        <div style={{display: 'flex', gap: 8}}>
          <input
            value={code}
            onChange={e => setCode(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && addCode()}
            placeholder="Type a plan code and press Enter"
            style={{
              width: '100%',
              padding: '7px 10px',
              fontSize: 13,
              border: '1px solid #D1D5DB',
              borderRadius: 6,
              outline: 'none',
              fontFamily: 'inherit',
              background: '#fff',
              color: '#111',
              boxSizing: 'border-box',
              fontFamily: 'monospace',
              letterSpacing: '0.02em',
              flex: 1,
            }}
          />
          <Button variant="secondary" onClick={addCode}>
            Add
          </Button>
        </div>
        {noCodesYet && name.trim() && (
          <span style={errorText}>At least one plan code is required.</span>
        )}
      </div>

      {codes.length > 0 && (
        <table style={tableStyle}>
          <tbody>
            {codes.map((c, i) => (
              <tr
                key={c}
                style={{
                  borderBottom:
                    i < codes.length - 1 ? '1px solid #F3F4F6' : 'none',
                  background: i % 2 === 0 ? '#fff' : '#F9FAFB',
                }}
              >
                <td style={tdCode}>{c}</td>
                <td style={tdAction}>
                  <Button
                    variant="text"
                    onClick={() => setCodes(cs => cs.filter(x => x !== c))}
                  >
                    Remove
                  </Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      <div style={footerRow}>
        <Button
          variant="primary"
          onClick={() => canSave && onSave(name.trim(), codes)}
          disabled={!canSave}
        >
          Create group
        </Button>
      </div>
    </>
  )
}

export function AddCodeModal({group, existingCodes, onSave, onClose}) {
  const [code, setCode] = useState('')
  const isDupe = existingCodes.includes(code.trim())
  const canSave = code.trim() && !isDupe

  return (
    <>
      <ParagraphText
        variant="reg"
        style={{margin: '0 0 12px', color: '#6B7280'}}
      >
        Adding plan code to <strong style={{color: '#111'}}>{group}</strong>
      </ParagraphText>

      <div style={fieldWrap}>
        <span style={fieldLabel}>Plan code</span>
        <input
          value={code}
          onChange={e => setCode(e.target.value)}
          onKeyDown={e => e.key === 'Enter' && canSave && onSave(code.trim())}
          placeholder="e.g. mc_pro-5-thryv-m2m"
          style={{
            width: '100%',
            padding: '7px 10px',
            fontSize: 13,
            border: '1px solid #D1D5DB',
            borderRadius: 6,
            outline: 'none',
            fontFamily: 'inherit',
            background: '#fff',
            color: '#111',
            boxSizing: 'border-box',
            fontFamily: 'monospace',
            letterSpacing: '0.02em',
          }}
          autoFocus
        />
        {isDupe && (
          <span style={errorText}>
            This plan code already exists in this group.
          </span>
        )}
      </div>

      <div style={footerRow}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => canSave && onSave(code.trim())}
          disabled={!canSave}
        >
          Add code
        </Button>
      </div>
    </>
  )
}

export function EditCodeModal({group, code, existingCodes, onSave, onClose}) {
  const [val, setVal] = useState(code)
  const isDupe = val.trim() !== code && existingCodes.includes(val.trim())
  const canSave = val.trim() && !isDupe

  return (
    <>
      <ParagraphText
        variant="reg"
        style={{margin: '0 0 12px', color: '#6B7280'}}
      >
        Editing plan code in <strong style={{color: '#111'}}>{group}</strong>
      </ParagraphText>

      <div style={fieldWrap}>
        <span style={fieldLabel}>Plan code</span>
        <input
          value={val}
          onChange={e => setVal(e.target.value)}
          style={{
            width: '100%',
            padding: '7px 10px',
            fontSize: 13,
            border: '1px solid #D1D5DB',
            borderRadius: 6,
            outline: 'none',
            fontFamily: 'inherit',
            background: '#fff',
            color: '#111',
            boxSizing: 'border-box',
            fontFamily: 'monospace',
            letterSpacing: '0.02em',
          }}
          autoFocus
        />
        {isDupe && (
          <span style={errorText}>
            This plan code already exists in this group.
          </span>
        )}
      </div>

      <div style={footerRow}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={() => canSave && onSave(val.trim())}
          disabled={!canSave}
        >
          Save
        </Button>
      </div>
    </>
  )
}

export function RemoveCodeModal({group, code, isLast, onConfirm, onClose}) {
  return (
    <>
      <ParagraphText
        variant="reg"
        style={{color: '#6B7280', margin: '0 0 1rem', lineHeight: 1.6}}
      >
        Remove{' '}
        <code
          style={{
            fontFamily: 'monospace',
            fontSize: 12,
            background: '#F3F4F6',
            padding: '1px 5px',
            borderRadius: 4,
          }}
        >
          {code}
        </code>{' '}
        from <strong style={{color: '#111'}}>{group}</strong>?
      </ParagraphText>

      {isLast && (
        <div
          style={{
            marginBottom: '1rem',
            padding: '8px 12px',
            background: '#FEF3C7',
            border: '1px solid #FDE68A',
            borderRadius: 6,
            fontSize: 12,
            color: '#92400E',
            lineHeight: 1.5,
          }}
        >
          ⚠️ This is the only plan code in <strong>{group}</strong>. Removing it
          will also delete the entire product group.
        </div>
      )}

      <div style={footerRow}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button
          variant="primary"
          onClick={onConfirm}
          style={{
            fontSize: 13,
            padding: '7px 15px',
            borderRadius: 6,
            border: 'none',
            background: '#DC2626',
            color: '#fff',
            cursor: 'pointer',
            fontWeight: 500,
          }}
        >
          {isLast ? 'Remove & delete group' : 'Remove'}
        </Button>
      </div>
    </>
  )
}

export function DeleteGroupModal({group, onConfirm, onClose}) {
  return (
    <>
      <ParagraphText
        variant="reg"
        style={{color: '#6B7280', margin: '0 0 1rem', lineHeight: 1.6}}
      >
        This will permanently delete{' '}
        <strong style={{color: '#111'}}>{group}</strong> and all its plan codes.
      </ParagraphText>

      <div style={footerRow}>
        <Button variant="secondary" onClick={onClose}>
          Cancel
        </Button>
        <Button variant="primary" onClick={onConfirm}>
          Delete
        </Button>
      </div>
    </>
  )
}
